import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Grid,
  Divider,
  Box
} from '@mui/material';

const CostSummaryCard = ({ donor }) => {
  // Calculate costs according to requirements
  const rawMaterialsCost = parseFloat(donor?.totalRawMaterialCost || 0);
  const qcTestingCost = parseFloat(donor?.totalQCTestingCost || 0);
  const totalDonorCost = rawMaterialsCost + qcTestingCost;
  
  // Calculate total vials manufactured
  const totalVialsManufactured = (donor?.manufacturedVials || [])
    .reduce((sum, v) => sum + (v.quantity || 0), 0);
  
  // Calculate QC samples used (sterility and endotoxin only)
  const qcSamplesUsed = (donor?.qcSamples || [])
    .filter(s => s.sampleType === 'STERILITY' || s.sampleType === 'ENDOTOXIN')
    .reduce((sum, s) => sum + (s.quantity || 0), 0);
  
  // Calculate vials available for sale (always calculate from manufactured - QC samples)
  // Don't rely on vialsAvailableAfterQC from backend as it may be outdated
  const vialsAvailableForSale = Math.max(0, totalVialsManufactured - qcSamplesUsed);
  
  // Calculate cost per vial
  const costPerVial = vialsAvailableForSale > 0 ? (totalDonorCost / vialsAvailableForSale) : 0;

  // Don't render if no cost data available
  if (!rawMaterialsCost && !qcTestingCost) {
    return null;
  }

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>
          Cost Summary
        </Typography>
        
        {/* Cost Breakdown */}
        <Grid container spacing={2}>
          <Grid item xs={6} sm={3}>
            <Typography variant="caption" color="text.secondary">Raw Materials</Typography>
            <Typography variant="h6">
              ${rawMaterialsCost.toFixed(2)}
            </Typography>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Typography variant="caption" color="text.secondary">QC Testing</Typography>
            <Typography variant="h6">
              ${qcTestingCost.toFixed(2)}
            </Typography>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Typography variant="caption" color="text.secondary">Total Donor Cost</Typography>
            <Typography variant="h5" color="primary" fontWeight="bold">
              ${totalDonorCost.toFixed(2)}
            </Typography>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Typography variant="caption" color="text.secondary">Cost Per Vial</Typography>
            <Typography variant="h6" color="secondary" fontWeight="bold">
              {costPerVial > 0 ? `$${costPerVial.toFixed(2)}` : 'N/A'}
            </Typography>
          </Grid>
        </Grid>
        
        {/* Vials Summary */}
        <Divider sx={{ my: 2 }} />
        <Grid container spacing={2}>
          <Grid item xs={4}>
            <Typography variant="caption" color="text.secondary">Total Manufactured</Typography>
            <Typography variant="h6">
              {totalVialsManufactured} vials
            </Typography>
          </Grid>
          <Grid item xs={4}>
            <Typography variant="caption" color="text.secondary">QC Samples Used</Typography>
            <Typography variant="h6">
              {qcSamplesUsed} vials
            </Typography>
          </Grid>
          <Grid item xs={4}>
            <Typography variant="caption" color="text.secondary">Available for Sale</Typography>
            <Typography variant="h6" color="success.main" fontWeight="bold">
              {vialsAvailableForSale} vials
            </Typography>
          </Grid>
        </Grid>

        {/* Cost Calculation Details */}
        {totalDonorCost > 0 && vialsAvailableForSale > 0 && (
          <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
            <Typography variant="caption" color="text.secondary" display="block">
              Cost Calculation: ${totalDonorCost.toFixed(2)} ÷ {vialsAvailableForSale} vials = ${costPerVial.toFixed(2)} per vial
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default CostSummaryCard;